import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import type { IconNamesType } from 'ksnm-common-ui/lib/Icon/types';

import { menuWidthByCss } from '@common/DesignLayout/DesignMenu/constantts';
import { appStore } from '@stores/appStore';
import { designDrawerStore } from '@stores/designDrawerStore';
import { designMenuStore } from '@stores/designMenuStore';

import { DesignBreadcrumbs } from './DesignBreadcrumbs';
import { DesignInnerMenu } from './DesignInnerMenu';
import { DesignInnerMenuDrawer } from './DesignInnerMenu/DesignInnerMenuDrawer';
import { DesignMenu } from './DesignMenu';

import styles from './DesignLayout.module.scss';

const heightPadding = 40;
const widthPadding = 36;

declare const REACT_APP_ENV: { [index: string]: string | null | undefined };

interface ScreenInterface {
  width: number;
  height: number;
}

interface DesignLayoutInterface {
  children?: JSX.Element | JSX.Element[] | string;
  title?: string | JSX.Element;
  menu?: { key: string; name: string; icon: IconNamesType; disabled?: boolean }[];
  onSelect?: (key: string) => void;
  selected?: string;
  scrollEnable?: boolean;
  className?: string;
  requiredHelp?: boolean; // выводит снизу подсказку по обязательным полям
}

function DesignLayoutComponent({
  children,
  title,
  menu,
  onSelect,
  selected,
  scrollEnable,
  className,
  requiredHelp,
}: DesignLayoutInterface): JSX.Element {
  const [screen, setScreen] = useState<ScreenInterface>({ width: -1, height: -1 });
  const [menuWidth, setMenuWidth] = useState(menuWidthByCss);

  const { form, errors } = designDrawerStore;
  const { blackoutBg, setBlackoutBg } = appStore;

  const {
    menuData: { length: menuDataLength },
    rmMenuData,
  } = designMenuStore;

  const detectScreenSize = useCallback(() => {
    setScreen(() => ({
      width: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
      height: window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight,
    }));
  }, [setScreen]);

  const contentWrapperStyle = useMemo(
    () => ({
      width: screen.width - menuWidth,
    }),
    [menuWidth, screen.width],
  );

  const contentStyle = useMemo(
    () => ({
      width: screen.width - menuWidth - widthPadding,
      height: screen.height - heightPadding * 2,
    }),
    [menuWidth, screen.height, screen.width],
  );

  useEffect(() => {
    detectScreenSize();
    window.addEventListener('resize', detectScreenSize);
    return () => {
      window.removeEventListener('resize', detectScreenSize);
    };
  }, [detectScreenSize]);

  const onCloseMenu = useCallback(() => {
    rmMenuData(true);
  }, [rmMenuData]);

  useEffect(() => {
    rmMenuData(true);
    return () => {
      rmMenuData(true);
    };
  }, [rmMenuData]);

  const titleOutput = useMemo(
    () => (typeof title === 'string' ? <p className={styles.title}>{title}</p> : title),
    [title],
  );

  return (
    <div className={classnames(styles.designLayout, className)}>
      <DesignMenu setMenuWidth={setMenuWidth} menuWidth={menuWidth} />
      <div
        className={classnames(styles.wrapper, 'designWrapper', {
          [styles.menuIsOpen]: menuDataLength === 1,
        })}
        style={contentWrapperStyle}
      >
        <div
          className={classnames({ [styles.lock]: menuDataLength > 0 })}
          onClick={onCloseMenu}
          onKeyDown={onCloseMenu}
          role="button"
          tabIndex={-1}
          aria-label="Close"
        />
        <div
          style={contentStyle}
          className={classnames(styles.contentWrapper, 'contentWrapper', { [styles.blur]: menuDataLength > 0 })}
        >
          <div className={classnames(styles.inner, 'innerContent')}>
            <DesignBreadcrumbs />
            <div
              className={classnames(styles.contentScrollableArea, 'contentScrollableArea', {
                [styles.scrollEnable]: scrollEnable,
              })}
            >
              <div className={classnames(styles.content, 'content')}>
                {titleOutput}
                {children}
              </div>
            </div>
            {menu && onSelect && <DesignInnerMenu menu={menu} onSelect={onSelect} selected={selected} />}
          </div>
        </div>
        <div className={styles.formHelpInfo}>
          {requiredHelp && (
            <div className={styles.required}>
              <p>*</p>
              <p>Обязательное поле для заполнения.</p>
            </div>
          )}
          {Object.values(errors).some((item) => item) && (
            <div className={styles.required}>
              <p className={styles.red}>*</p>
              <p>Незаполненное обязательное поле</p>
            </div>
          )}
          {(REACT_APP_ENV.REACT_APP_VERSION || process.env.REACT_APP_VERSION) && (
            <div
              className={styles.version}
              title={`Версия ${REACT_APP_ENV.REACT_APP_VERSION ?? process.env.REACT_APP_VERSION}`}
            >
              Версия {REACT_APP_ENV.REACT_APP_VERSION ?? process.env.REACT_APP_VERSION}
            </div>
          )}
        </div>
      </div>
      {form !== null && <DesignInnerMenuDrawer />}
      <div className={classnames({ [styles.blackoutBg]: blackoutBg })} onClick={() => setBlackoutBg(false)} />
    </div>
  );
}

export const DesignLayout = observer(DesignLayoutComponent);
